import CheckCircle from 'assets/check-circle.svg';
import ErrorCircle from 'assets/exclamation-x-circle.svg';
import { ReactComponent as InformationCircle } from 'assets/information-circle.svg';
import personCircle from 'assets/person-circle.svg';
import { AxiosError } from 'axios';
import LeaveConfirm from 'components/LeaveConfirm';
import { useAuth } from 'contexts/AuthProvider';
import { useToast } from 'contexts/ToastProvider';
import { CbtUserUpdateDto } from 'dto/cbt-user-update.dto';
import { useCallbackPrompt } from 'hook/useCallbackPrompt';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getRoleText } from 'utils/helper';
import { getItem } from 'utils/localStorageHelper';
import { Role } from '..';
import AccountType from './AccountType';
import CurrentPasswordGroup from './CurrentPasswordGroup';

export interface UserUpdateFormData extends CbtUserUpdateDto {
  email?: string;
  confirmPassword?: string;
}

const MyAccount = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { logout, user, putCbtUserUpdate, getUser } = useAuth();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [isOwner, setIsOwner] = useState<boolean>(false);
  const [usernameEditable, setUserNameEditable] = useState<boolean>(false);
  const [passwordEditable, setPasswordEditable] = useState<boolean>(false);
  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(showDialog);
  const methods = useForm<UserUpdateFormData>();

  const getUserInfo = () => {
    setIsOwner(user.role === Role.Owner);
    const userInfo: UserUpdateFormData = {
      email: user.email,
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      name: user.name,
      cloud: user.cloud,
      role: user.role,
    };
    methods.reset(userInfo);
  };

  useEffect(() => {
    getUserInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (methods.formState.isDirty && !methods.formState.isSubmitSuccessful) {
      setShowDialog(true);
    } else {
      setShowDialog(false);
    }
  }, [methods.formState.isDirty, methods.formState.isSubmitSuccessful]);

  const resetForm = () => {
    methods.reset();
    setPasswordEditable(false);
    setUserNameEditable(false);
  };

  const onSubmit = (data: UserUpdateFormData) => {
    if (data.name === '') {
      data.name = user.email;
    }

    /* NOTE: PUT /api/users/cbtUserUpdate/{id} requst body properties都必要，
             當僅修改 User name，需要輸入 currentPassword 給 API 做驗證，故 newPassword 直接填入 currentPassword
     */
    if (data.newPassword === '') {
      data.newPassword = data.currentPassword;
    }

    data.cloud = user.cloud;

    delete data.email;
    delete data.confirmPassword;

    putCbtUserUpdate(data)
      .then(() => {
        resetForm();
        getUser();
        addToast({
          title: t('toast.updatedSuccessfully'),
          message: '',
          color: 'bg-toast-success-50',
          icon: CheckCircle,
          text: 'text-toast-success-800',
          svg: 'toastIcon-success',
        });
        if (getItem('isFromCloud') && data.cloud) {
          logout();
        }
      })
      .catch((error: AxiosError<{ status: number; statusCode: string; message: string }>) => {
        let message: string = error.response?.data.message ?? '';
        addToast({
          title: t('toast.updatedFailed'),
          message,
          color: 'bg-toast-error-50',
          icon: ErrorCircle,
          text: 'text-toast-error-800',
          svg: 'toastIcon-error',
        });
      });
  };

  return (
    <div className="card-body setting">
      <FormProvider {...methods}>
        <form className="flex flex-col" onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="flex justify-between items-center border-b p-6">
            <div className="flex items-center space-x-3">
              <div className="avatar mask mask-squircle w-12 h-12">
                <img src={personCircle} alt="Avatar Tailwind CSS Component" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm opacity-50">
                  <span className="capitalize">{getRoleText(user.role)}</span>
                </span>
                <span className="font-bold">{user.email}</span>
              </div>
            </div>
            <button type="button" className="btn btn-primary" onClick={logout}>
              {t('modal.btnLogOut')}
            </button>
          </div>
          <div className="label px-6 border-b border-slate-300">
            <label className="label-text w-64">{t('profile.formUserName')}</label>
            <div className="flex flex-col space-y-1 flex-1">
              {usernameEditable ? (
                <>
                  <input type="text" placeholder={user.email} className="input max-w-xs" {...methods.register(`name`)} />
                  {methods.formState.errors.name && <span className="span-error px-1">{methods.formState.errors.name.message}</span>}
                </>
              ) : (
                <p>{user.name}</p>
              )}
            </div>
            {!usernameEditable && (
              <button type="button" className="btn btn-link text-[#489EE5]" onClick={() => setUserNameEditable(true)}>
                {t('modal.btnRename')}
              </button>
            )}
          </div>
          <div className="label px-6 border-b border-slate-300">
            <label className="flex items-center label-text w-64">
              {t('profile.formEmail')}
              <div className="ml-1 tooltip tooltip-right text-left" data-tip={t('profile.formEmailTip')}>
                <InformationCircle className="w-4 h-4 text-gray-400" />
              </div>
            </label>
            <p>{user.email}</p>
          </div>
          <CurrentPasswordGroup passwordEditable={passwordEditable} usernameEditable={usernameEditable} setPasswordEditable={setPasswordEditable} />
          <AccountType cloud={user.cloud} isOwner={isOwner} getUserInfo={getUserInfo} />
          {(methods.formState.isDirty || passwordEditable || usernameEditable) && (
            <div className="flex space-x-2 justify-end mt-5">
              <button type="button" className="btn btn-outline" onClick={resetForm}>
                {t('modal.btnCancel')}
              </button>
              <button type="submit" className="btn btn-primary" disabled={!methods.formState.isDirty}>
                {t('modal.btnApply')}
              </button>
            </div>
          )}
        </form>
        <LeaveConfirm
          // @ts-ignore
          showDialog={showPrompt}
          confirmNavigation={confirmNavigation}
          cancelNavigation={cancelNavigation}
        />
      </FormProvider>
    </div>
  );
};

export default MyAccount;
